
import React from "react";
import { useParams, Link } from "react-router-dom";
import { useVerifyWebinarAccessQuery } from "../../app/api/customFunnelApi";
import { useInitializePaymentMutation } from "../../app/api/dBCApi";
import WebinarPage, { handleInitializeResponseRedirect } from "../common/WebinarPage";

// ── Video ─────────────────────────────────────────────────────────────────────
const EMBED_ID = "Q0eV0jv2PA4";

// ── Pricing (kobo) ────────────────────────────────────────────────────────────
const FULL_PRICE   = 19789300;
const SPLIT_FIRST  = 8333300;
const SPLIT_SECOND = 8333300;
const SPLIT_THIRD = 8333300;
// ── Payment trigger ───────────────────────────────────────────────────────────
const PAYMENT_TRIGGER_SECONDS = 300; // 5 minutes

// ── Features ──────────────────────────────────────────────────────────────────
const FEATURES = [
  "Full sales pipeline automation",
  "Lead capture & intelligent scoring",
  "Email + WhatsApp follow-up sequences",
];


// ─── Loading skeleton ─────────────────────────────────────────────────────────

const LoadingSkeleton: React.FC = () => (
  <div className="min-h-screen bg-white flex flex-col">
    <header className="bg-gradient-to-r from-blue-900 to-blue-700 h-16" />
    <main className="flex-1 flex items-center justify-center px-4">
      <div className="w-full max-w-2xl space-y-4 animate-pulse">
        <div className="h-6 bg-gray-200 rounded-full w-1/3 mx-auto" />
        <div className="h-10 bg-gray-200 rounded-full w-2/3 mx-auto" />
        <div className="h-4 bg-gray-100 rounded-full w-1/2 mx-auto" />
        <div className="h-64 bg-gray-100 rounded-2xl mt-6" />
      </div>
    </main>
  </div>
);

// ─── Access denied screen ─────────────────────────────────────────────────────

const AccessDenied: React.FC<{ message?: string }> = ({ message }) => (
  <div className="min-h-screen bg-white flex flex-col">
    <header className="bg-gradient-to-r from-blue-900 to-blue-700 text-white sticky top-0 z-50 shadow-lg">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
        <Link to="/" className="text-xl font-extrabold tracking-tight">Launch Kit Naija</Link>
      </div>
    </header>

    <main className="flex-1 flex items-center justify-center px-4 py-16">
      <div className="text-center max-w-md w-full bg-white rounded-2xl shadow-2xl p-10">

        {/* Lock icon */}
        <div className="w-20 h-20 rounded-full bg-red-50 border-2 border-red-200 flex items-center justify-center mx-auto mb-6">
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
            <path d="M12 16V12a6 6 0 1 1 12 0v4" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" />
            <rect x="7" y="16" width="22" height="14" rx="3" fill="#fee2e2" stroke="#ef4444" strokeWidth="2" />
            <circle cx="18" cy="23" r="2" fill="#ef4444" />
          </svg>
        </div>

        <span className="inline-block px-3 py-1 bg-red-50 text-red-600 text-xs font-bold rounded-full uppercase tracking-widest mb-4">
          Access Required
        </span>

        <h2 className="text-2xl font-bold text-gray-900 mb-3">
          This demo is invite-only
        </h2>

        <p className="text-gray-600 text-sm leading-relaxed mb-2">
          {message || "Your access link is invalid or has expired."}
        </p>
        <p className="text-gray-500 text-sm leading-relaxed mb-8">
          Register below to get your personal access link sent directly to your email and WhatsApp — it only takes 30 seconds.
        </p>

        <Link
          to="/sales-funnel/sales/demo-request"
          className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-full shadow-lg transition-colors text-sm"
        >
          Get Free Demo Access →
        </Link>

        <p className="mt-4 text-xs text-gray-400">Already registered? Check your email for the access link.</p>
      </div>
    </main>
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const DemoViewPage: React.FC = () => {
  const { uniqueId } = useParams<{ uniqueId: string }>();

  // If no uniqueId in the URL at all, deny immediately
  if (!uniqueId) {
    return <AccessDenied message="No access token was provided in this URL." />;
  }

  return <DemoViewPageInner uniqueId={uniqueId} />;
};

/**
 * Inner component — keeps the hook call unconditional (Rules of Hooks).
 * When the uniqueId is valid, renders WebinarPage with payment trigger.
 */
const DemoViewPageInner: React.FC<{ uniqueId: string }> = ({ uniqueId }) => {
  // Both hooks must be called unconditionally before any early returns
  const { data, isLoading, isError } = useVerifyWebinarAccessQuery(uniqueId);
  const [initializePaymentMutation] = useInitializePaymentMutation();

  //const firstName = data?.firstName || "there";

  if (isLoading) return <LoadingSkeleton />;
  if (isError || !data?.valid) return <AccessDenied message={data?.message} />;

  const initializePayment = async (payload: {
    email: string;
    name: string;
    amount: number;
    paymentPlan: "full" | "split";
    metadata?: Record<string, any>;
  }) => {
    const res = await initializePaymentMutation(payload).unwrap();
    const redirected = handleInitializeResponseRedirect(res);
    if (!redirected) {
      alert(
        (res as any)?.message ??
          "Payment initialized but no redirect URL was returned. Please contact support.",
      );
    }
    return res;
  };

  return (
    <WebinarPage
      embedId={EMBED_ID}
      initializePayment={initializePayment}
      fullPrice={FULL_PRICE}
      splitFirst={SPLIT_FIRST}
      splitSecond={SPLIT_SECOND}
      splitThird={SPLIT_THIRD}
      features={FEATURES}
      paymentTriggerSeconds={PAYMENT_TRIGGER_SECONDS}
      courseName="Sales System — Full Platform Demo"
      sourceTag="demo_5min"
    />
  );
};

export default DemoViewPage;